import { GetStaticPaths, GetStaticProps } from 'next';
import { convertInCurrencyValue, skipStaticPageGeneration } from 'utility/functions';
import { fetchCasinoBySlug, fetchCasinoList } from 'utility/casino/utils';

import { GameDetailSpecProps } from 'components/gameDetail/gameDetailSpec/GameDetailSpec';
import { GamesSkillGameDto } from 'types/swagger';
import { Layout } from 'components/Layout';
import PageContent from 'components/page-content/casino-gioco-slug/PageContent';
import { Seo } from 'components/seo';
import { cleanConfig } from 'lib/datoCms/utils';
import { generateSeoMetaTags } from 'components/seo';
import { getAllMightLikes } from 'lib/datoCms/queries/getAllMightLikes';
import { getDatoCMSData } from 'sites/snai/lib/datoCms/queries/getDatoCMSData';
import { ISR_REVALIDATE } from 'utility/constant';
import { getCasinoTopPage } from 'lib/datoCms/queries/getCasinoTopPage';
import { getCardsFromSearchField } from 'lib/ssr/search';
import { Category } from 'types/categoryEnum';

const CasinoSlug = ({
  site,
  footer,
  casino,
  header,
  needHelp,
  canonical,
  seoMetaTags,
  sidebarInstantGame,
  allMightLikes,
  choseFromSnaiCardsSearch,
}: any) => {
  return (
    <Layout
      header={header}
      needHelp={needHelp}
      footer={footer}
      mightLikes={allMightLikes}
      sidebarInstantGame={sidebarInstantGame}
    >
      <Seo siteSeo={site} metaTags={seoMetaTags} canonicalUrl={canonical} />
      <PageContent casino={casino} choseFromSnaiCardsSearch={choseFromSnaiCardsSearch} />
    </Layout>
  );
};

export const getStaticPaths: GetStaticPaths = async () => {
  if (skipStaticPageGeneration()) {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
  const data = await fetchCasinoList();
  const paths = data?.games?.map((game: GamesSkillGameDto) => ({
    params: {
      slug: encodeURIComponent(`${game.slug}`),
    },
  }));

  return {
    paths,
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
  const { slug } = params ?? {};

  console.log(`## casino.Gioco.getStaticProps ${slug}`);

  try {
    const prCasinoTop = getCasinoTopPage(locale);
    const prCmsData = getDatoCMSData('common', locale);
    const prPageData = fetchCasinoBySlug(slug);

    const [cmsData, pageData, casinoTop] = await Promise.all([prCmsData, prPageData, prCasinoTop]);

    if (!pageData) {
      console.log(`casino-slug-gioco-${locale}: "${slug}" : NOT FOUND `);
      return {
        notFound: true,
      };
      // } else {
      //   console.log(`casino-slug-gioco-${locale}: "${slug}" `, pageData)
    }

    const {
      casinoTopPage: { searchField },
    } = casinoTop ?? {};
    const { site, footer, header, config, needHelp, allLabels: labels, sidebarInstantGame } = cmsData ?? {};

    const prMightLikes = getAllMightLikes(locale);
    const cardsPr = getCardsFromSearchField(searchField ?? [], Category.Casino);
    const [mightLikes, searchCards] = await Promise.all([prMightLikes, cardsPr]);

    const { allMightLikes } = mightLikes;

    const {
      image,
      isNew,
      isExclusive,
      description,
      tema,
      title,
      produttore,
      jackpot,
      playInfo,
      codicePiattaforma,
      isInstant,
      promo,
      hasDemo,
      gameMode,
      idProduttore,
      launchType,
    } = pageData ?? {};

    const specs: GameDetailSpecProps[] = [
      {
        label: 'tema',
        values: tema ? [...tema] : null,
      },
      {
        label: 'produttore',
        values: produttore ? [produttore] : null,
        id: idProduttore,
      },
      {
        label: 'jackpot',
        values: jackpot ? [String(convertInCurrencyValue(jackpot))] : null,
      },
      {
        label: 'gamemode',
        values: gameMode ? [...gameMode] : null,
      },
    ];

    const seoMetaTags = generateSeoMetaTags(title, description);

    return {
      props: {
        site,
        seoMetaTags,
        canonical: '',
        header,
        needHelp,
        footer,
        labels,
        allMightLikes,
        sidebarInstantGame,
        casino: {
          title,
          coverImage: {
            src: image && image.srcCover ? image.srcCover : '',
            alt: image && Reflect.get(image, 'alt') ? Reflect.get(image, 'alt') : title,
          },
          isNew,
          isExclusive,
          description,
          specs,
          playInfo,
          codicePiattaforma,
          isInstant,
          promo,
          hasDemo,
          launchType,
        },
        choseFromSnaiCardsSearch: searchCards,
        config: cleanConfig(config),
      },
      revalidate: ISR_REVALIDATE,
    };
  } catch (err: any) {
    return { props: { errors: err.message } };
  }
};

export default CasinoSlug;
